// Ported from relay-protocol packages/relay-widget (v0.1.0).
// Typed client for the orchestrator's stateless REST API tier. Every
// endpoint here corresponds 1:1 with a route registered in
// orchestrator/internal/api/server.go -- see ./types.ts for the wire
// shapes, hand mirrored from that Go package's JSON tags. Ported from
// frontend/src/api.ts, decoupled from import.meta.env: baseUrl is passed
// explicitly to createRelayClient so this module has no framework or
// bundler-env coupling (no React, no wagmi, no import.meta.env).
import type {
  ApiErrorBody,
  Catalog,
  DepositAddressRequest,
  DepositAddressResult,
  Order,
  OrderSummary,
  QuoteRequest,
  QuoteResult,
  RecoveryInfo,
  RecoveryRequest,
} from './types'

/** Thrown for any non-2xx API response. `status` is the HTTP status code. */
export class RelayApiError extends Error {
  status: number
  /** Parsed JSON error body when the response was valid JSON, else undefined. */
  body?: unknown

  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.name = 'RelayApiError'
    this.status = status
    this.body = body
  }
}

export interface RelayClientOptions {
  /** API origin, e.g. "https://orchestrator.example.com". Trailing slashes are stripped. */
  baseUrl: string
}

export interface RelayClient {
  /** GET /v1/config -- the curated origin catalog + fixed crossd destination. */
  getConfig(): Promise<Catalog>
  /** POST /v1/quote -- a price-only preview, no order/deposit is created. */
  getQuote(req: QuoteRequest): Promise<QuoteResult>
  /**
   * GET /v1/deposit-address -- resolves the stable, reusable deposit
   * address for (user, originChainId, originCurrency). No amount is
   * involved and no order is created; the same address can receive any
   * number of deposits of any size. Several deposits can be aggregated into
   * one order when they jointly fund the same reusable forwarder.
   *
   * `target` (Task 8) is the delivery asset's symbol from
   * `Catalog.targets` -- sent as `&target=<symbol>` only when it's a
   * non-empty string; omitted otherwise, in which case the backend
   * resolves the default target (byte-identical to pre-Task-6 behavior).
   */
  getDepositAddress(req: DepositAddressRequest, target?: string): Promise<DepositAddressResult>
  /**
   * GET /v1/orders/{orderId} -- current status + stepper progress for one
   * order. Reads are public (unauthenticated).
   */
  getOrder(orderId: string): Promise<Order>
  /**
   * GET /v1/orders?user=... -- every order swept from deposits to that
   * user's standing address(es) (newest-first, capped server-side).
   */
  listOrders(user: string): Promise<OrderSummary[]>
  /** Absolute URL of the SSE order-change stream for `user`
   * (GET /v1/orders/stream?user=...). Emits `event: connected` on open,
   * `event: orders-changed` when that user's orders change, and `:
   * heartbeat` comments -- it carries no order data itself, so consumers
   * always follow up with `listOrders`. */
  ordersStreamUrl(user: string): string
  /**
   * GET /v1/recovery?user=... -- permissionless self-recovery status for a
   * user's forwarder on BSC (Task 1). `req.target` (optional) selects which
   * per-target forwarder/factory to report -- omitted selects the
   * configured default, byte-identical to pre-multi-target behavior.
   * Purely informational -- fetching it never triggers or blocks any
   * on-chain action. The response's `tokens[]` (Task 6/7 -- every known BSC
   * token held at the forwarder) flows through as-is: it's plain JSON, so no
   * extra parsing/mapping is needed beyond RecoveryInfo's own typing, and
   * it's simply absent/undefined for an older backend that predates it.
   */
  getRecovery(req: RecoveryRequest): Promise<RecoveryInfo>
}

/**
 * Builds a framework-agnostic Relay API client. Uses plain `fetch` only --
 * no react-query, no RainbowKit/wagmi. `baseUrl` is passed in explicitly (no
 * `import.meta.env` coupling) so the widget can be embedded in any host app's
 * own config/env story.
 *
 * No API key: the backend has no inbound key gate (upstream 83845fb removed
 * the dead `apiKey`/X-API-Key plumbing -- a security control that does not
 * exist must not appear in a public type).
 */
export function createRelayClient(options: RelayClientOptions): RelayClient {
  const baseUrl = options.baseUrl.replace(/\/+$/, '')

  // The body is read as text (rather than res.json()) so non-2xx responses
  // with a JSON {"error": ...} body and non-JSON error bodies can both be
  // turned into a useful RelayApiError.
  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const headers: Record<string, string> = {
      ...(init?.headers as Record<string, string> | undefined),
    }
    // Only set Content-Type when there's a request body -- bodiless GETs
    // (getConfig, getOrder) send no Content-Type, mirroring the original
    // frontend/src/api.ts request shapes.
    if (init?.body != null) {
      headers['Content-Type'] = 'application/json'
    }

    let res: Response
    try {
      res = await fetch(`${baseUrl}${path}`, { ...init, headers })
    } catch (err) {
      throw new RelayApiError(0, `network error calling ${path}: ${(err as Error).message}`)
    }

    const text = await res.text()

    if (!res.ok) {
      let message = `request to ${path} failed with status ${res.status}`
      let parsedBody: unknown
      try {
        parsedBody = JSON.parse(text) as ApiErrorBody
        const errBody = parsedBody as ApiErrorBody
        if (errBody?.error) message = errBody.error
      } catch {
        // Non-JSON or empty error body -- keep the generic message above,
        // and fall back to the raw text so callers still see something.
        parsedBody = text || undefined
      }
      throw new RelayApiError(res.status, message, parsedBody)
    }

    // A 2xx body is expected to be JSON -- but "expected" is not "guaranteed"
    // once anything can sit between the app and the API. A captive portal, a CDN
    // interstitial or a misrouted proxy answers 200 with HTML, and a bare
    // JSON.parse then threw a raw SyntaxError straight out of the SDK, bypassing
    // the RelayApiError contract every other failure path honors and surfacing
    // in consumer apps as an unhandled exception (advisory ADV-002).
    //
    // Folded into the same error type as every other failure, with the raw body
    // attached so the caller can see what actually arrived.
    try {
      return JSON.parse(text) as T
    } catch {
      throw new RelayApiError(
        res.status,
        `expected a JSON response from ${path} but got ${text ? 'a non-JSON body' : 'an empty body'}`,
        text || undefined,
      )
    }
  }

  return {
    getConfig(): Promise<Catalog> {
      // `targets` (Task 8) defaults to [] when the backend response omits
      // it entirely -- back-compat with an older backend that predates
      // per-target delivery (Task 4).
      return request<Catalog>('/v1/config').then((catalog) => ({
        ...catalog,
        targets: catalog.targets ?? [],
      }))
    },

    getQuote(req: QuoteRequest): Promise<QuoteResult> {
      return request<QuoteResult>('/v1/quote', {
        method: 'POST',
        body: JSON.stringify(req),
      })
    },

    getDepositAddress(req: DepositAddressRequest, target?: string): Promise<DepositAddressResult> {
      const params = new URLSearchParams({
        user: req.user,
        originChainId: String(req.originChainId),
        originCurrency: req.originCurrency,
      })
      if (target) {
        params.set('target', target)
      }
      return request<DepositAddressResult>(`/v1/deposit-address?${params.toString()}`)
    },

    getOrder(orderId: string): Promise<Order> {
      return request<Order>(`/v1/orders/${encodeURIComponent(orderId)}`)
    },

    listOrders(user: string): Promise<OrderSummary[]> {
      const params = new URLSearchParams({ user })
      // `?? []` for the same reason getConfig defaults `targets`: a body that
      // omits (or nulls) the key must not hand `undefined` to OrdersList, which
      // maps over the result.
      return request<{ orders?: OrderSummary[] | null }>(`/v1/orders?${params.toString()}`).then(
        (res) => res.orders ?? [],
      )
    },

    ordersStreamUrl(user: string): string {
      return `${baseUrl}/v1/orders/stream?user=${encodeURIComponent(user)}`
    },

    getRecovery(req: RecoveryRequest): Promise<RecoveryInfo> {
      const params = new URLSearchParams({ user: req.user })
      if (req.target) {
        params.set('target', req.target)
      }
      return request<RecoveryInfo>(`/v1/recovery?${params.toString()}`)
    },
  }
}
